/**
 * Driver Table Component
 * Responsive table/card view for drivers with actions
 */

import { useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import { Card } from '../../ui/card';
import { ComplianceBadge } from '../ComplianceBadge';
import { AssignRouteModal } from './AssignRouteModal';
import { CertificationModal } from './CertificationModal';
import type { DriverDTO } from '../../../services/driver.service';

interface DriverTableProps {
  drivers: DriverDTO[];
}

export function DriverTable({ drivers }: DriverTableProps) {
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isCertModalOpen, setIsCertModalOpen] = useState(false);

  const handleAssignRoute = (driverId: string) => {
    setSelectedDriverId(driverId);
    setIsAssignModalOpen(true);
  };

  const handleCertifications = (driverId: string) => {
    setSelectedDriverId(driverId);
    setIsCertModalOpen(true);
  };

  return (
    <>
      {/* Desktop View */}
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Emp #</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>CDL</TableHead>
              <TableHead>Route</TableHead>
              <TableHead>Compliance</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {drivers.map((driver) => (
              <TableRow key={driver.id}>
                <TableCell className="font-medium">{driver.employeeNumber}</TableCell>
                <TableCell>
                  {driver.firstName} {driver.lastName}
                  {driver.email && (
                    <div className="text-xs text-gray-500">{driver.email}</div>
                  )}
                </TableCell>
                <TableCell>{driver.phone}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      driver.status === 'active'
                        ? 'success'
                        : driver.status === 'on_leave'
                        ? 'warning'
                        : 'secondary'
                    }
                  >
                    {driver.status.replace('_', ' ')}
                  </Badge>
                </TableCell>
                <TableCell>
                  Class {driver.cdl.class}
                  <div className="text-xs text-gray-500">
                    Exp: {new Date(driver.cdl.expirationDate).toLocaleDateString()}
                  </div>
                </TableCell>
                <TableCell>
                  {driver.routeAssignment ? (
                    <div className="text-sm">{driver.routeAssignment.routeName}</div>
                  ) : (
                    <button
                      onClick={() => handleAssignRoute(driver.id)}
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      Assign Route
                    </button>
                  )}
                </TableCell>
                <TableCell>
                  <ComplianceBadge isCompliant={driver.isCompliant} />
                  <button
                    onClick={() => handleCertifications(driver.id)}
                    className="mt-1 text-xs text-primary-600 hover:text-primary-700"
                  >
                    View Certs ({driver.certifications.length})
                  </button>
                </TableCell>
                <TableCell>
                  <button className="rounded-md p-1 hover:bg-gray-100">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile View */}
      <div className="grid gap-4 lg:hidden">
        {drivers.map((driver) => (
          <Card key={driver.id} className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold">
                  {driver.firstName} {driver.lastName}
                </div>
                <div className="text-sm text-gray-600">{driver.employeeNumber}</div>
              </div>
              <Badge
                variant={
                  driver.status === 'active'
                    ? 'success'
                    : driver.status === 'on_leave'
                    ? 'warning'
                    : 'secondary'
                }
              >
                {driver.status.replace('_', ' ')}
              </Badge>
            </div>

            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Phone:</span>
                <span>{driver.phone}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">CDL:</span>
                <div className="text-right">
                  <div>Class {driver.cdl.class}</div>
                  <div className="text-xs text-gray-500">
                    Exp: {new Date(driver.cdl.expirationDate).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Route:</span>
                {driver.routeAssignment ? (
                  <span>{driver.routeAssignment.routeName}</span>
                ) : (
                  <button
                    onClick={() => handleAssignRoute(driver.id)}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    Assign
                  </button>
                )}
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600">Compliance:</span>
                <ComplianceBadge isCompliant={driver.isCompliant} />
              </div>

              <div className="pt-2">
                <button
                  onClick={() => handleCertifications(driver.id)}
                  className="text-primary-600 hover:text-primary-700"
                >
                  View Certifications ({driver.certifications.length})
                </button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Modals */}
      {selectedDriverId && (
        <>
          <AssignRouteModal
            driverId={selectedDriverId}
            open={isAssignModalOpen}
            onClose={() => {
              setIsAssignModalOpen(false);
              setSelectedDriverId(null);
            }}
          />
          <CertificationModal
            driverId={selectedDriverId}
            open={isCertModalOpen}
            onClose={() => {
              setIsCertModalOpen(false);
              setSelectedDriverId(null);
            }}
          />
        </>
      )}
    </>
  );
}
